import { PR_ITEM_MODEL, getPooledQuantity } from "../create-pr/helper";

// Pure port of create-pr/index.js's old loadExistingPR() body -- transforms a raw
// GET /purchase-request/:id response into the flat shape EditPR's state setters consume,
// instead of calling 8 different setState functions inline.
export const mapPRResponseToFormState = (pr) => {
  const isHeadOffice = pr.prScope === "HEAD_OFFICE";

  const formDetails = {
    prDate: pr.prDate ? pr.prDate.split("T")[0] : new Date().toISOString().split("T")[0],
    department: pr.department ? { _id: pr.department, name: pr.departmentName } : null,
    preferredVendorCode: pr.preferredVendorCode || "",
    preferredVendorName: pr.preferredVendorName || "",
    preferredVendor: null,
    paymentTerms: pr.paymentTerms || "",
    vendorRemarks: pr.vendorRemarks || "",
    justification: pr.justification || "",
    remarks: pr.remarks || "",
  };

  let procurementDestination = "SELF";
  let hoContextSiteId = "";
  let selectedStores = [];
  let selectedCities = [];
  let selectedStates = [];
  let selectedRegions = [];

  if (isHeadOffice) {
    procurementDestination = ["SPECIFIC_STORES", "CITY", "STATE", "REGION"].includes(
      pr.storeSelectionType
    )
      ? pr.storeSelectionType
      : "SELF";
    // pr.site arrives populated (the backend's findOne/find hooks always populate it), so
    // it's an object with its own `.id`, never a scalar -- unwrap it the same way item-level
    // refs below are unwrapped.
    hoContextSiteId = String(pr.site?.id ?? pr.site ?? "");
    if (procurementDestination !== "SELF" && pr.selectedSites?.length) {
      selectedStores = pr.selectedSites.map((s) =>
        typeof s === "object" ? s : { _id: s, name: "", code: "" }
      );
    }
    if (procurementDestination === "CITY") {
      selectedCities = pr.selectedCities || [];
    } else if (procurementDestination === "STATE") {
      selectedStates = pr.selectedStates || [];
    } else if (procurementDestination === "REGION") {
      selectedRegions = pr.selectedRegions || [];
    }
  }

  // The backend populates item.item/item.lineItem/item.category/item.subCategory on every
  // fetched PR item -- each a full object (its own .id/.name/...), never a scalar id. Group
  // by the populated lineItem's own id, and unwrap item into the scalar `item` id + `_item`
  // object field PRItemRow's Item Autocomplete already expects (same shape
  // mapPrFileUploadResponse produces for an uploaded row).
  //
  // category/subCategory/categoryMappingId are unwrapped to plain ids here so every write
  // path (buildEditPayload, single-store-row.js's merge-by-item-id, PRItemRow's Subcategory
  // Autocomplete lookup) can treat them the same as a freshly-resolved row, regardless of
  // whether the PR item came from the backend's own resolveAndStampCategoryMappings (every
  // PR successfully saved through this feature already has them) or -- for a PR item that
  // predates category-mapping stamping entirely -- falls back to the nested
  // item.item.subCategory/category (the Item master's own deprecated single mirror,
  // unconditionally populated by Item's model hooks), the closest available reconstruction
  // for those older rows.
  const prItemsByLineItem = {};
  const lineItemIdSet = new Set();
  (pr.items || []).forEach((item, idx) => {
    const lineItemId = item.lineItem?.id ?? "misc";
    lineItemIdSet.add(lineItemId);

    const legacySubCategory = item.item?.subCategory ?? null;
    const legacyCategory = item.item?.category ?? null;
    const categoryId =
      item.category?.id ?? item.category?._id ?? item.category ??
      (legacyCategory?.id ?? legacyCategory?._id ?? legacyCategory ?? null);
    const subCategoryId =
      item.subCategory?.id ?? item.subCategory?._id ?? item.subCategory ??
      (legacySubCategory?.id ?? legacySubCategory?._id ?? legacySubCategory ?? null);
    // Display object for PRItemRow's Subcategory Autocomplete -- prefers the populated
    // top-level ref (the pair actually stamped on this PR item) and only falls back to the
    // legacy mirror when there's no top-level object to show a name from.
    const subCategoryObj = (typeof item.subCategory === "object" && item.subCategory) || legacySubCategory;

    if (!prItemsByLineItem[lineItemId]) prItemsByLineItem[lineItemId] = [];
    prItemsByLineItem[lineItemId].push({
      ...PR_ITEM_MODEL,
      ...item,
      item: item.item?.id ?? null,
      _item: item.item ?? null,
      category: categoryId,
      subCategory: subCategoryId,
      categoryMappingId: item.categoryMappingId || undefined,
      _subCategory: subCategoryObj,
      _subCategoryId: subCategoryId,
      _tempId: `edit-${lineItemId}-${idx}`,
    });
  });

  return {
    loadedPrStatus: pr.status || "",
    prType: pr.prType || "",
    prScope: pr.prScope || "SINGLE_STORE",
    isHeadOffice,
    // The PR's own persisted site identity (same fields pr-details/index.js reads for its
    // read-only header) -- authoritative regardless of scope, unlike budgetDetails.site
    // (a pooled STORE for HEAD_OFFICE) or a storeList lookup that may not have loaded yet.
    siteName: pr.siteName || "",
    siteCode: pr.siteCode || "",
    creatorName: pr.creatorName || "",
    financialYear: pr.financialYear || "",
    headerRequiredByDate: pr.requiredByDate ? pr.requiredByDate.split("T")[0] : "",
    formDetails,
    attachments: pr.documentLinks || [],
    procurementDestination,
    hoContextSiteId,
    selectedStores,
    selectedCities,
    selectedStates,
    selectedRegions,
    prItemsByLineItem,
    lineItemIds: [...lineItemIdSet],
    // pr.budget is also populated (see hoContextSiteId above) -- unwrap to a scalar id so
    // loadBudgetForEdit's GET_BUDGETS `_id` filter actually matches instead of serializing
    // an object into the query string.
    budgetId: pr.budget?.id ?? pr.budget ?? null,
    departmentId: pr.department || null,
  };
};

// Pure port of create-pr/index.js's old buildPayload() edit-only branch -- Edit (PUT) only
// accepts a narrow field set server-side (see updatePR validation): prDate, items,
// preferredVendorCode/Name, vendorRemarks, justification, documentLinks, plus
// hoContextSiteId (HEAD_OFFICE only). Allocations are stripped to the client-writable
// fields only -- reservedQuantity/convertedQuantity/shortClosedQuantity are backend-owned
// (allocation.service.js) and the backend's Joi schema forbids sending them, even though a
// PR loaded via mapPRResponseToFormState still carries them from the server's own document.
export const buildEditPayload = ({
  prItemsByLineItem,
  isHeadOffice,
  formDetails,
  attachments,
  hoContextSiteId,
  headerRequiredByDate,
}) => {
  const allItems = [];
  Object.entries(prItemsByLineItem).forEach(([lineItemId, items]) => {
    items.forEach((item) => {
      const pooledQuantity = getPooledQuantity(item);
      if (!isHeadOffice && pooledQuantity === 0) return;

      const allocations = (item.allocations || [])
        .filter((a) => a.requestedQuantity !== undefined && a.requestedQuantity !== null)
        .map((a) => ({
          allocationId: a.allocationId || undefined,
          storeId: a.storeId,
          requestedQuantity: parseFloat(a.requestedQuantity),
        }));
      const hasAllocations = allocations.length > 0;

      allItems.push({
        // item.id (not item._id) is this row's real PurchaseRequisitionItem identifier --
        // every other read of a PR item's own id in this codebase (purchase-order/helper.js,
        // edit-po/index.js, create-po/index.js) uses `.id`, never `._id`; the backend never
        // actually serializes `_id` on a PR item (see mapPRResponseToFormState's `...item`
        // spread just above, which carries `.id` through as-is). Sending `_id: undefined`
        // here made every edit-save look like a brand new item to the backend, silently
        // losing this item's stable identity -- and with it, any PO's prItemId reference
        // into it (surfacing as a missing Balance PR Quantity on the linked PO's Edit
        // screen once the PR had been edited/reconsidered).
        _id: item.id || item._id || undefined,
        item: item.item,
        // Same reasoning as create-pr/index.js's buildPayload: category/subCategory/
        // categoryMappingId are resolved once, up front, against the item's actual
        // categoryMappings (auto-populate-items, add-item-modal.js's Subcategory+Item
        // selection, or the PR's own previously-persisted values carried through by
        // mapPRResponseToFormState's `...item` spread) -- never guessed here.
        category: item.category ?? undefined,
        subCategory: item.subCategory ?? undefined,
        categoryMappingId: item.categoryMappingId || undefined,
        lineItem: lineItemId === "misc" ? undefined : lineItemId,
        budgetHead: item.budgetHead || "",
        description: item.description || "",
        hsnCode: item.hsnCode || "",
        uom: item.uom || "",
        ...(hasAllocations ? {} : { quantity: pooledQuantity }),
        estimatedRate: parseFloat(item.estimatedRate) || 0,
        estimatedAmount: item.estimatedAmount || 0,
        taxRate: parseFloat(item.taxRate) || 0,
        estimatedTaxAmount: item.estimatedTaxAmount || 0,
        estimatedTotalAmount: item.estimatedTotalAmount || 0,
        remarks: item.remarks || "",
        rateContract: item.rateContract || false,
        vendorCode: item.vendorCode || "",
        vendorName: item.vendorName || "",
        requiredByDate: item.requiredByDate || headerRequiredByDate || null,
        ...(hasAllocations ? { allocations } : {}),
      });
    });
  });

  return {
    prDate: formDetails.prDate,
    items: allItems,
    preferredVendorCode: formDetails.preferredVendorCode || "",
    preferredVendorName: formDetails.preferredVendorName || "",
    vendorRemarks: formDetails.vendorRemarks || "",
    justification: formDetails.justification || "",
    documentLinks: attachments,
    ...(isHeadOffice ? { hoContextSiteId: Number(hoContextSiteId) } : {}),
  };
};
